/**
 * OfflineBanner Component
 * Shows a persistent banner when the user is offline.
 * Also shows sync status when coming back online with pending items.
 *
 * AI-4714: Offline UI indicators
 */

import { useOfflineStatus } from '@/hooks/useOfflineStatus';

export function OfflineBanner() {
  const { isOnline, pendingSyncCount, isSyncing, lastSyncResult } = useOfflineStatus();

  // Show sync success briefly
  if (isOnline && lastSyncResult && lastSyncResult.synced > 0 && pendingSyncCount === 0) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[100] bg-green-600 text-white text-center py-2 px-4 text-sm font-medium animate-in slide-in-from-top duration-300">
        <div className="flex items-center justify-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>{lastSyncResult.synced} item{lastSyncResult.synced !== 1 ? 's' : ''} synced successfully</span>
        </div>
      </div>
    );
  }

  // Show syncing indicator
  if (isOnline && isSyncing) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[100] bg-blue-600 text-white text-center py-2 px-4 text-sm font-medium">
        <div className="flex items-center justify-center gap-2">
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span>Syncing offline changes...</span>
        </div>
      </div>
    );
  }

  // Show offline banner
  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-600 text-white text-center py-2 px-4 text-sm font-medium">
        <div className="flex items-center justify-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.536 8.464a5 5 0 010 7.072M8.464 15.536a5 5 0 010-7.072"
            />
            <circle cx="12" cy="12" r="1" fill="currentColor" />
          </svg>
          <span>
            You're offline
            {pendingSyncCount > 0
              ? ` — ${pendingSyncCount} change${pendingSyncCount !== 1 ? 's' : ''} pending sync`
              : ' — cached content available'}
          </span>
        </div>
      </div>
    );
  }

  // Online with pending items (sync hasn't started yet)
  if (pendingSyncCount > 0) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium">
        <div className="flex items-center justify-center gap-2">
          <span>{pendingSyncCount} offline change{pendingSyncCount !== 1 ? 's' : ''} waiting to sync</span>
        </div>
      </div>
    );
  }

  return null;
}
